import * as React from 'react';

// Base UI typeahead and reconciliation both emit reason `none` without a
// native event. Trigger handlers mark the keydown phase; item registration
// clears it before CompositeList reconciliation, including consumer flushSync.
export const SelectTriggerKeydownContext = React.createContext<{
  inKeydown: React.RefObject<boolean>;
  itemValues: Map<unknown, number>;
} | null>(null);

function isTypeaheadKey(
  event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey'>,
) {
  return (
    event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey
  );
}

export function useSelectTriggerKeydown({
  onKeyDownCapture,
  onKeyDown,
}: Pick<React.HTMLAttributes<HTMLElement>, 'onKeyDownCapture' | 'onKeyDown'>) {
  const inKeydown = React.useContext(SelectTriggerKeydownContext)?.inKeydown;
  const timerRef = React.useRef<{ win: Window; id: number } | null>(null);
  const clearPending = React.useCallback(() => {
    const pending = timerRef.current;
    timerRef.current = null;
    pending?.win.clearTimeout(pending.id);
  }, []);
  React.useEffect(
    () => () => {
      clearPending();
      if (inKeydown) inKeydown.current = false;
    },
    [clearPending, inKeydown],
  );
  const classify = (event: React.KeyboardEvent<HTMLElement>) => {
    if (inKeydown) {
      inKeydown.current =
        isTypeaheadKey(event) &&
        event.currentTarget.getAttribute('aria-expanded') !== 'true';
    }
  };
  return {
    onKeyDownCapture(event: React.KeyboardEvent<HTMLElement>) {
      onKeyDownCapture?.(event);
      const win = event.currentTarget.ownerDocument.defaultView;
      if (!inKeydown || !win) return;
      classify(event);
      clearPending();
      timerRef.current = {
        win,
        id: win.setTimeout(() => {
          timerRef.current = null;
          inKeydown.current = false;
        }, 0),
      };
    },
    onKeyDown(event: React.KeyboardEvent<HTMLElement>) {
      onKeyDown?.(event);
      // Re-arm after consumer flushSync, before Base UI handles the same key.
      classify(event);
    },
  };
}

export function useSelectItemRegistration(value: unknown) {
  const context = React.useContext(SelectTriggerKeydownContext);
  React.useLayoutEffect(() => {
    if (!context) return;
    const { inKeydown, itemValues } = context;
    inKeydown.current = false;
    itemValues.set(value, (itemValues.get(value) ?? 0) + 1);
    // Runs before Base UI's CompositeList layout effect, even when only a
    // descendant renders. Never mutate classification during root render.
    return () => {
      inKeydown.current = false;
      const count = (itemValues.get(value) ?? 1) - 1;
      if (count) itemValues.set(value, count);
      else itemValues.delete(value);
    };
  }, [context, value]);
}

// Keep requested typeahead matches separate from the committed value.
export function useSelectTypeaheadDedup(
  trigger: React.RefObject<HTMLElement | null>,
) {
  const inKeydown = React.useRef(false);
  const [keydownContext] = React.useState(() => ({
    inKeydown,
    itemValues: new Map<unknown, number>(),
  }));
  const session = React.useRef<{
    lastMatch: { value: unknown } | null;
    expiresAt: number;
    matched: boolean;
  }>({ lastMatch: null, expiresAt: 0, matched: false });

  React.useLayoutEffect(() => {
    const element = trigger.current;
    if (!element) return;
    const reset = () => {
      session.current.lastMatch = null;
      session.current.expiresAt = 0;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        !isTypeaheadKey(event) ||
        element.getAttribute('aria-expanded') === 'true'
      )
        return;
      const now = Date.now();
      // No callback for the previous character means Base UI found no match
      // and cleared its prefix. The next character starts a fresh session.
      if (now >= session.current.expiresAt || !session.current.matched) reset();
      // Match Base UI's 750ms idle reset, including keys that do not match.
      session.current.expiresAt = now + 750;
      session.current.matched = false;
    };
    element.addEventListener('keydown', onKeyDown, true);
    element.addEventListener('blur', reset);
    return () => {
      element.removeEventListener('keydown', onKeyDown, true);
      element.removeEventListener('blur', reset);
    };
  });

  const isDuplicate = React.useCallback((value: unknown) => {
    session.current.matched = true;
    const duplicate =
      Date.now() < session.current.expiresAt &&
      session.current.lastMatch !== null &&
      Object.is(value, session.current.lastMatch.value);
    // Record the request before consumer callbacks, even if they cancel it.
    session.current.lastMatch = { value };
    return duplicate;
  }, []);
  const isTypeahead = React.useCallback(
    (details?: { reason?: string; event?: Event }) =>
      details?.reason === 'none' &&
      inKeydown.current &&
      // Autofill can run in a microtask before the keydown flag's timer clears.
      (!details.event || details.event.type === 'base-ui'),
    [],
  );
  return { isDuplicate, isTypeahead, keydownContext };
}
